/**
 * One purchase invoice or return, as posted -- and the two things that can
 * still happen to it: goods sent back (a return, line by line, never more
 * than came in), or the whole document cancelled with a reason.
 */
import { useState } from 'react'
import { Link as RouterLink, useLocation, useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
  Paper, Skeleton, Stack, TextField, Typography,
} from '@mui/material'
import { useTranslation } from 'react-i18next'

import { cancelPurchase, fetchPurchase, returnPurchase } from '../api/endpoints'
import { errorMessage } from '../api/client'
import { P, type PurchaseDetail } from '../api/types'
import { useAuth } from '../store/AuthContext'
import { useUnsavedChanges } from '../store/UnsavedChanges'
import ScreenTitle from '../components/ScreenTitle'
import VoucherDrawer from '../reports/VoucherDrawer'
import { Figure, FigureGrid } from '../reports/SummaryFigures'
import { useBi } from '../components/Bi'
import { gregorian, toHijri } from '../reports/hijri'
import { today } from '../dates'
import { addMoney, mulMoney, sanitize } from '../vouchers/money'
import { c, fmtMoney } from '../theme'
import { lineAmount } from './qty'

export default function PurchaseView() {
  const { id } = useParams()
  const { t, i18n } = useTranslation()
  const bi = useBi()
  const nav = useNavigate()
  const loc = useLocation()
  const qc = useQueryClient()
  const { can } = useAuth()
  const ar = i18n.language === 'ar'
  const q = useQuery({ queryKey: ['inv-purchase', Number(id)], queryFn: () => fetchPurchase(Number(id)) })
  const [returning, setReturning] = useState(false)
  const [cancelOpen, setCancelOpen] = useState(false)
  const [voucher, setVoucher] = useState<number | null>(null)
  const saved = (loc.state as { saved?: boolean } | null)?.saved

  if (q.isLoading) return <Paper variant="outlined" sx={{ p: 3 }}><Skeleton /><Skeleton /></Paper>
  if (q.isError || !q.data) return <Alert severity="warning">{errorMessage(q.error)}</Alert>
  const p = q.data
  const isReturn = p.kind === 'RETURN'
  const live = p.status === 'POSTED'
  const canReturn = !isReturn && live && can(P.postPurchase)
    && p.lines.some((l) => Number(l.returnable) > 0)

  return (
    <Stack spacing={2}>
      {saved && <Alert severity="success">{t('inv.purchaseSaved', { doc: p.doc_no })}</Alert>}
      <Paper variant="outlined">
        <ScreenTitle titleKey={isReturn ? 'inv.purchaseReturn' : 'inv.purchaseInvoice'}
          sub={<>
            <span className="doc-no">{p.doc_no}</span> · {gregorian(p.doc_date, i18n.language)}
            {' · '}{toHijri(p.doc_date, i18n.language)}
          </>}
          right={
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {!live && <Chip color="error" variant="outlined" label={t('inv.cancelled')} />}
              {p.voucher_id && (
                <Button variant="outlined" onClick={() => setVoucher(p.voucher_id!)}>
                  {t('inv.viewVoucher')}</Button>
              )}
              {canReturn && !returning && (
                <Button variant="outlined" onClick={() => setReturning(true)}>{t('inv.returnGoods')}</Button>
              )}
              {live && can(P.reverse) && (
                <Button color="error" variant="outlined" onClick={() => setCancelOpen(true)}>
                  {t('inv.cancel')}</Button>
              )}
              {can(P.postPurchase) && !isReturn && (
                <Button variant="contained" onClick={() => nav('/inventory/purchases/new')}>
                  {t('inv.newPurchase')}</Button>
              )}
            </Stack>
          } />

        <Box sx={{ px: 2.5, pt: 1, pb: 2, display: 'grid', gap: 2,
                   gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' } }}>
          <Field label={t('inv.supplier')} value={(ar && p.supplier_name_ar) || p.supplier_name}
            sub={p.supplier_code} />
          <Field label={t('inv.supplierInvoiceNo')} value={p.supplier_invoice_no ?? '—'} />
          <Field label={isReturn ? t('inv.fromStore') : t('inv.intoStore')} value={p.store_name} />
          <Field label={p.paid_from ? t('inv.paidFrom') : t('inv.dueDate')}
            value={p.paid_from ?? (p.due_date ? gregorian(p.due_date, i18n.language) : '—')} />
          {p.against_no && (
            <Field label={t('inv.againstInvoice')} value={
              <RouterLink to={`/inventory/purchases/${p.against_id}`}>{p.against_no}</RouterLink>} />
          )}
          {p.notes && <Box sx={{ gridColumn: '1 / -1' }}><Field label={t('inv.notes')} value={p.notes} /></Box>}
        </Box>

        {returning ? (
          <ReturnForm p={p} onDone={(r) => {
            setReturning(false)
            void qc.invalidateQueries({ queryKey: ['inv-purchase'] })
            void qc.invalidateQueries({ queryKey: ['inv-purchases'] })
            void qc.invalidateQueries({ queryKey: ['inv-items'] })
            nav(`/inventory/purchases/${r.id}`, { state: { saved: true } })
          }} onCancel={() => setReturning(false)} />
        ) : (
          <Box sx={{ overflowX: 'auto' }}>
            <table className="rpt" style={{ minWidth: 760 }}>
              <thead><tr>
                <th style={{ width: 32 }}>#</th>
                <th>{t('inv.item')}</th>
                <th className="num" style={{ width: 100 }}>{t('inv.qty')}</th>
                <th className="num" style={{ width: 110 }}>{t('inv.costPerUnit')}</th>
                <th className="num" style={{ width: 96 }}>{t('inv.discount')}</th>
                <th className="num" style={{ width: 110 }}>{t('inv.net')}</th>
                <th className="num" style={{ width: 90 }}>{t('inv.vat')}</th>
                <th className="num" style={{ width: 110 }}>{t('inv.total')}</th>
              </tr></thead>
              <tbody>
                {p.lines.map((l) => (
                  <tr key={l.id}>
                    <td className="num" style={{ color: 'var(--ink-muted)' }}>{l.line_no}</td>
                    <td>
                      <div dir="auto"><span className="doc-no">{l.item_code}</span> · {(ar && l.item_name_ar) || l.item_name}</div>
                      {!isReturn && Number(l.returned) > 0 && (
                        <div style={{ fontSize: 12, color: 'var(--warning-ink)' }}>
                          {t('inv.returnedSoFar', { qty: l.returned, unit: l.unit })}</div>
                      )}
                    </td>
                    <td className="num">{l.qty} <span style={{ color: 'var(--ink-muted)', fontSize: 12 }}>{l.unit}</span></td>
                    <td className="num">{l.unit_cost}</td>
                    <td className="num">{Number(l.discount) ? fmtMoney(l.discount) : ''}</td>
                    <td className="num">{fmtMoney(l.net)}</td>
                    <td className="num">{fmtMoney(l.vat_amount)}</td>
                    <td className="num">{fmtMoney(l.total)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Box>
        )}

        {!returning && (
          <Box sx={{ pt: 2 }}>
            <FigureGrid>
              <Figure label={bi('inv.subtotal')} value={fmtMoney(p.subtotal)} />
              <Figure label={bi('inv.vat')} value={fmtMoney(p.vat_amount)} />
              <Figure label={bi('inv.total')} value={fmtMoney(p.total)} strong />
            </FigureGrid>
          </Box>
        )}
      </Paper>

      {!isReturn && (p.returns?.length ?? 0) > 0 && (
        <Paper variant="outlined" sx={{ p: 2.5 }}>
          <Typography component="h2" sx={{ m: 0, fontSize: 15, fontWeight: 600, mb: 1 }}>
            {t('inv.returnsAgainst')}</Typography>
          {p.returns!.map((r) => (
            <Stack key={r.id} direction="row" justifyContent="space-between"
              sx={{ py: 0.75, borderBottom: `1px solid ${c.divider}`, opacity: r.status === 'CANCELLED' ? 0.5 : 1 }}>
              <RouterLink to={`/inventory/purchases/${r.id}`}><span className="doc-no">{r.doc_no}</span></RouterLink>
              <span>{gregorian(r.doc_date, i18n.language)}</span>
              <span className="num">−{fmtMoney(r.total)}</span>
            </Stack>
          ))}
        </Paper>
      )}

      <CancelDialog open={cancelOpen} p={p} onClose={() => setCancelOpen(false)} onDone={() => {
        setCancelOpen(false)
        void qc.invalidateQueries({ queryKey: ['inv-purchase'] })
        void qc.invalidateQueries({ queryKey: ['inv-purchases'] })
        void qc.invalidateQueries({ queryKey: ['inv-items'] })
      }} />
      <VoucherDrawer voucherId={voucher} onClose={() => setVoucher(null)} />
    </Stack>
  )
}

function Field({ label, value, sub }: { label: string; value: React.ReactNode; sub?: string }) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography sx={{ fontSize: 12, color: c.inkMuted }}>{label}</Typography>
      <Typography dir="auto" sx={{ fontSize: 14.5, fontWeight: 500 }}>{value}</Typography>
      {sub && <Typography className="doc-no" sx={{ fontSize: 12, color: c.inkMuted }}>{sub}</Typography>}
    </Box>
  )
}

function ReturnForm({ p, onDone, onCancel }: {
  p: PurchaseDetail; onDone: (r: PurchaseDetail) => void; onCancel: () => void
}) {
  const { t, i18n } = useTranslation()
  const ar = i18n.language === 'ar'
  const [qty, setQty] = useState<Record<number, string>>({})
  const [date, setDate] = useState(today())
  const [notes, setNotes] = useState('')
  const [error, setError] = useState<string | null>(null)
  const chosen = p.lines.filter((l) => Number(qty[l.id] || 0) > 0)
  useUnsavedChanges(chosen.length > 0)

  // The same proportion the server takes: (net / qty) x returned qty.
  const lineNet = (l: PurchaseDetail['lines'][number], q: string) =>
    Number(q) === Number(l.qty) ? l.net : lineAmount(q, String(Number(l.net) / Number(l.qty)))
  const nets = chosen.map((l) => lineNet(l, qty[l.id]))
  const sub = addMoney('0', ...nets)
  const vat = addMoney('0', ...chosen.map((l, i) => mulMoney(nets[i], String(Number(l.vat_rate) / 100))))
  const over = p.lines.find((l) => Number(qty[l.id] || 0) > Number(l.returnable))

  const save = useMutation({
    mutationFn: () => returnPurchase(p.id, { doc_date: date, notes: notes.trim() || null,
      lines: chosen.map((l) => ({ line_id: l.id, qty: qty[l.id] })) }),
    onSuccess: onDone,
    onError: (e) => setError(errorMessage(e)),
  })

  return (
    <Box>
      <Alert severity="info" sx={{ mx: 2.5, mb: 1.5 }}>{t('inv.returnHelp')}</Alert>
      {error && <Alert severity="error" sx={{ mx: 2.5, mb: 1.5 }}>{error}</Alert>}
      <Box sx={{ overflowX: 'auto' }}>
        <table className="rpt" style={{ minWidth: 640 }}>
          <thead><tr>
            <th>{t('inv.item')}</th>
            <th className="num" style={{ width: 110 }}>{t('inv.bought')}</th>
            <th className="num" style={{ width: 110 }}>{t('inv.canReturn')}</th>
            <th className="num" style={{ width: 130 }}>{t('inv.returnQty')}</th>
            <th className="num" style={{ width: 110 }}>{t('inv.net')}</th>
          </tr></thead>
          <tbody>
            {p.lines.map((l) => (
              <tr key={l.id}>
                <td dir="auto"><span className="doc-no">{l.item_code}</span> · {(ar && l.item_name_ar) || l.item_name}</td>
                <td className="num">{l.qty} {l.unit}</td>
                <td className="num">{l.returnable} {l.unit}</td>
                <td style={{ padding: '4px 8px' }}>
                  <TextField size="small" value={qty[l.id] ?? ''} disabled={Number(l.returnable) <= 0}
                    error={Number(qty[l.id] || 0) > Number(l.returnable)}
                    onChange={(e) => setQty({ ...qty, [l.id]: sanitize(e.target.value) })}
                    inputProps={{ inputMode: 'decimal', className: 'num', style: { textAlign: 'end' },
                                  'aria-label': t('inv.returnQtyOf', { item: l.item_code }) }} />
                </td>
                <td className="num">{Number(qty[l.id] || 0) > 0 ? fmtMoney(lineNet(l, qty[l.id])) : ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Box>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}
        sx={{ px: 2.5, py: 2 }}>
        <TextField type="date" size="small" label={t('entry.date')} value={date}
          InputLabelProps={{ shrink: true }} onChange={(e) => setDate(e.target.value)} />
        <TextField size="small" label={t('inv.reasonNote')} value={notes} sx={{ flex: 1 }}
          onChange={(e) => setNotes(e.target.value)} />
        <Typography className="num" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
          {t('inv.returnTotal', { amount: fmtMoney(addMoney(sub, vat)) })}</Typography>
        <Button color="inherit" onClick={onCancel}>{t('common.cancel')}</Button>
        <Button variant="contained" disabled={!chosen.length || !!over || save.isPending}
          onClick={() => save.mutate()}>{t('inv.saveReturn')}</Button>
      </Stack>
    </Box>
  )
}

function CancelDialog({ open, p, onClose, onDone }: {
  open: boolean; p: PurchaseDetail; onClose: () => void; onDone: () => void
}) {
  const { t } = useTranslation()
  const [reason, setReason] = useState('')
  const [error, setError] = useState<string | null>(null)
  const go = useMutation({
    mutationFn: () => cancelPurchase(p.id, reason.trim()),
    onSuccess: () => { setReason(''); onDone() },
    onError: (e) => setError(errorMessage(e)),
  })
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{t('inv.cancelTitle', { doc: p.doc_no })}</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Typography sx={{ fontSize: 14, mb: 2 }}>{t('inv.cancelBody')}</Typography>
        <TextField autoFocus fullWidth label={t('inv.cancelReason')} value={reason}
          onChange={(e) => setReason(e.target.value)} />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button color="inherit" onClick={onClose}>{t('common.close')}</Button>
        <Button color="error" variant="contained" disabled={reason.trim().length < 3 || go.isPending}
          onClick={() => go.mutate()}>{t('inv.cancelConfirm')}</Button>
      </DialogActions>
    </Dialog>
  )
}
